import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Users,
  Phone,
  MessageSquare,
  Trophy,
  XCircle,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  RefreshCw,
  ArrowRight,
  PhoneMissed,
  Save,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { CardHeader, CardTitle } from '../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

const formatPct = (v) => v != null ? `${v}%` : '0%';

function StatCard({ title, current, previous, change, icon: Icon, color, subtitle }) {
  const isUp = change > 0;
  const isDown = change < 0;

  return (
    <Card className="bg-white border-stone-100 shadow-sm" data-testid={`stat-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className={`p-2 rounded-lg ${color}`}>
            <Icon className="w-5 h-5" />
          </div>
          {change !== null && change !== undefined && (
            <div className={`flex items-center gap-1 text-sm font-semibold rounded-full px-2.5 py-0.5 ${
              isUp ? 'bg-green-50 text-green-700' : isDown ? 'bg-red-50 text-red-700' : 'bg-stone-50 text-stone-500'
            }`}>
              {isUp ? <ArrowUpRight className="w-3.5 h-3.5" /> : isDown ? <ArrowDownRight className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
              {isUp ? '+' : ''}{change}%
            </div>
          )}
        </div>
        <p className="text-3xl font-bold text-stone-900 mb-1">{current}</p>
        <p className="text-xs text-stone-500 font-medium">{title}</p>
        <p className="text-[11px] text-stone-400 mt-1">Prev: {previous}</p>
        {subtitle && <p className="text-[11px] text-stone-400 mt-0.5">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}

function ConversionCard({ label, from, to, rate, prevRate, change }) {
  const isUp = change > 0;
  const isDown = change < 0;

  return (
    <Card className="bg-white border-stone-100 shadow-sm" data-testid={`conversion-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-medium text-stone-500 bg-stone-100 px-2 py-0.5 rounded">{from}</span>
          <ArrowRight className="w-3.5 h-3.5 text-stone-400" />
          <span className="text-xs font-medium text-stone-500 bg-stone-100 px-2 py-0.5 rounded">{to}</span>
        </div>
        <div className="flex items-end justify-between">
          <div>
            <p className="text-3xl font-bold text-stone-900">{formatPct(rate)}</p>
            <p className="text-xs text-stone-500 font-medium mt-1">{label}</p>
            <p className="text-[11px] text-stone-400 mt-0.5">Prev: {formatPct(prevRate)}</p>
          </div>
          {change !== null && change !== undefined && change !== 0 && (
            <div className={`flex items-center gap-1 text-sm font-semibold rounded-full px-2.5 py-0.5 ${
              isUp ? 'bg-green-50 text-green-700' : isDown ? 'bg-red-50 text-red-700' : 'bg-stone-50 text-stone-500'
            }`}>
              {isUp ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
              {isUp ? '+' : ''}{change}%
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const PERIODS = [
  { key: 'current_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
];

export default function SalesStatsTab() {
  const [period, setPeriod] = useState('current_month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState({ current: {}, previous: {} });
  const [missedCancelled, setMissedCancelled] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, savedRes] = await Promise.all([
        fetch(`${API}/api/sales-stats?period=${period}`),
        fetch(`${API}/api/sales-stats/saved?period=${period}`),
      ]);
      if (statsRes.ok) setData(await statsRes.json());
      if (savedRes.ok) {
        const s = await savedRes.json();
        setSaved(s);
        setMissedCancelled(s.current?.missedCancelled != null ? String(s.current.missedCancelled) : '');
      }
    } catch (e) {
      console.error('Failed to fetch sales stats:', e); // eslint-disable-line no-console
    }
    setLoading(false);
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/sales-stats/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          missedCancelled: missedCancelled ? Number(missedCancelled) : null,
          period,
        }),
      });
      await fetchData();
    } catch (e) {
      console.error('Failed to save:', e); // eslint-disable-line no-console
    }
    setSaving(false);
  };

  const curr = data?.current || {};
  const prev = data?.previous || {};
  const changes = data?.changes || {};

  const formatRange = (range) => {
    if (!range) return '';
    const s = new Date(range.start);
    const e = new Date(range.end);
    const opts = { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' };
    return `${s.toLocaleDateString('en-GB', opts)} – ${e.toLocaleDateString('en-GB', opts)}`;
  };

  return (
    <div className="space-y-6" data-testid="sales-stats-tab">
      {/* Header + Period Selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold text-stone-800">Sales Pipeline Stats</h2>
          <p className="text-sm text-stone-500">
            {data ? formatRange(data.currentRange) : 'Loading...'}
            {data && <span className="text-stone-400"> vs {formatRange(data.previousRange)}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-stone-100 rounded-lg p-0.5" data-testid="period-selector">
            {PERIODS.map(p => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  period === p.key
                    ? 'bg-white text-stone-900 shadow-sm'
                    : 'text-stone-500 hover:text-stone-700'
                }`}
                data-testid={`period-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <Button
            onClick={fetchData}
            disabled={loading}
            variant="outline"
            size="sm"
            className="border-stone-200"
            data-testid="refresh-stats"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Pipeline Funnel Cards - All Deals */}
      <div>
        <h3 className="text-sm font-semibold text-stone-600 uppercase tracking-wider mb-3">All Deals</h3>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <StatCard
            title="Leads"
            current={curr.leads || 0}
            previous={prev.leads || 0}
            change={changes.leads}
            icon={Users}
            color="bg-blue-50 text-blue-600"
            subtitle="New deals created"
          />
          <StatCard
            title="Had Call"
            current={curr.hadCall || 0}
            previous={prev.hadCall || 0}
            change={changes.hadCall}
            icon={Phone}
            color="bg-teal-50 text-teal-600"
            subtitle="Proven call evidence"
          />
          <StatCard
            title="Missed / Cancelled"
            current={saved.current?.missedCancelled || 0}
            previous={saved.previous?.missedCancelled || 0}
            change={saved.previous?.missedCancelled > 0 ? Math.round(((saved.current?.missedCancelled || 0) - saved.previous.missedCancelled) / saved.previous.missedCancelled * 1000) / 10 : (saved.current?.missedCancelled > 0 ? 100 : 0)}
            icon={PhoneMissed}
            color="bg-yellow-50 text-yellow-600"
            subtitle="Manual input"
          />
          <StatCard
            title="Quote Sent"
            current={curr.quoteSent || 0}
            previous={prev.quoteSent || 0}
            change={changes.quoteSent}
            icon={MessageSquare}
            color="bg-purple-50 text-purple-600"
            subtitle={curr.quoteSentRevenue ? `£${(curr.quoteSentRevenue).toLocaleString('en-GB')} quoted` : 'Quote given'}
          />
          <StatCard
            title="Won"
            current={curr.won || 0}
            previous={prev.won || 0}
            change={changes.won}
            icon={Trophy}
            color="bg-green-50 text-green-600"
            subtitle={curr.wonRevenue ? `£${(curr.wonRevenue).toLocaleString('en-GB')} total` : 'Deals closed'}
          />
          <StatCard
            title="Lost"
            current={curr.lost || 0}
            previous={prev.lost || 0}
            change={changes.lost}
            icon={XCircle}
            color="bg-red-50 text-red-600"
            subtitle="Deals lost"
          />
        </div>
      </div>

      {/* Pipeline Funnel Cards - Created This Period */}
      <div>
        <h3 className="text-sm font-semibold text-stone-600 uppercase tracking-wider mb-3">Leads Created This Month Only</h3>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <StatCard
            title="Leads"
            current={curr.leads || 0}
            previous={prev.leads || 0}
            change={changes.leads}
            icon={Users}
            color="bg-blue-50 text-blue-600"
            subtitle="New deals created"
          />
          <StatCard
            title="Had Call"
            current={curr.hadCallNew || 0}
            previous={prev.hadCallNew || 0}
            change={changes.hadCallNew}
            icon={Phone}
            color="bg-teal-50 text-teal-600"
            subtitle="New leads with proven call"
          />
          <StatCard
            title="Missed / Cancelled"
            current={saved.current?.missedCancelled || 0}
            previous={saved.previous?.missedCancelled || 0}
            change={null}
            icon={PhoneMissed}
            color="bg-yellow-50 text-yellow-600"
            subtitle="Manual input"
          />
          <StatCard
            title="Quote Sent"
            current={curr.quoteSentNew || 0}
            previous={prev.quoteSentNew || 0}
            change={changes.quoteSentNew}
            icon={MessageSquare}
            color="bg-purple-50 text-purple-600"
            subtitle={curr.quoteSentNewRevenue ? `£${(curr.quoteSentNewRevenue).toLocaleString('en-GB')} quoted` : 'Quote given'}
          />
          <StatCard
            title="Won"
            current={curr.wonNew || 0}
            previous={prev.wonNew || 0}
            change={changes.wonNew}
            icon={Trophy}
            color="bg-green-50 text-green-600"
            subtitle={curr.wonNewRevenue ? `£${(curr.wonNewRevenue).toLocaleString('en-GB')} total` : 'Deals closed'}
          />
          <StatCard
            title="Lost"
            current={curr.lostNew || 0}
            previous={prev.lostNew || 0}
            change={changes.lostNew}
            icon={XCircle}
            color="bg-red-50 text-red-600"
            subtitle="New leads lost"
          />
        </div>
      </div>

      {/* Conversion Rates */}
      <div>
        <h3 className="text-sm font-semibold text-stone-600 uppercase tracking-wider mb-3">Conversion Rates</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConversionCard
            label="Lead to Call"
            from="Leads"
            to="Had Call"
            rate={curr.leadToCall}
            prevRate={prev.leadToCall}
            change={changes.leadToCall}
          />
          <ConversionCard
            label="Call to Won"
            from="Had Call"
            to="Won"
            rate={curr.callToWon}
            prevRate={prev.callToWon}
            change={changes.callToWon}
          />
          <ConversionCard
            label="Lead to Won"
            from="Leads"
            to="Won"
            rate={curr.leadToWon}
            prevRate={prev.leadToWon}
            change={changes.leadToWon}
          />
        </div>
      </div>

      {/* Manual Input */}
      <Card className="bg-white border-stone-100 shadow-sm" data-testid="sales-stats-manual-input">
        <CardHeader className="pb-3 border-b border-stone-100">
          <CardTitle className="text-base font-medium text-stone-800">Monthly Input</CardTitle>
          <p className="text-xs text-stone-500 mt-1">Enter missed or cancelled calls for {period === 'current_month' ? 'this' : 'last'} month</p>
        </CardHeader>
        <CardContent className="p-5">
          <div className="flex items-end gap-4 max-w-xs">
            <div className="flex-1">
              <label className="text-xs font-medium text-stone-600 mb-1.5 block">Missed / Cancelled Calls</label>
              <Input
                type="number"
                value={missedCancelled}
                onChange={e => setMissedCancelled(e.target.value)}
                placeholder="e.g. 5"
                data-testid="input-missed-cancelled"
              />
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-stone-900 text-white hover:bg-stone-800"
              data-testid="save-stats-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Visual Funnel */}
      <Card className="bg-white border-stone-100 shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-sm font-semibold text-stone-600 uppercase tracking-wider mb-5">Pipeline Funnel</h3>
          <div className="space-y-3">
            {[
              { label: 'Leads', value: curr.leads || 0, color: 'bg-blue-500' },
              { label: 'Had Call', value: curr.hadCall || 0, color: 'bg-teal-500' },
              { label: 'Missed/Cancelled', value: saved.current?.missedCancelled || 0, color: 'bg-yellow-500' },
              { label: 'Quote Sent', value: curr.quoteSent || 0, color: 'bg-purple-500' },
              { label: 'Won', value: curr.won || 0, color: 'bg-green-500' },
              { label: 'Lost', value: curr.lost || 0, color: 'bg-red-400' },
            ].map(stage => {
              const maxVal = Math.max(curr.leads || 1, 1);
              const width = Math.max((stage.value / maxVal) * 100, 2);
              return (
                <div key={stage.label} className="flex items-center gap-3">
                  <span className="text-xs text-stone-600 font-medium w-24 text-right">{stage.label}</span>
                  <div className="flex-1 bg-stone-100 rounded-full h-8 overflow-hidden">
                    <div
                      className={`h-full ${stage.color} rounded-full flex items-center justify-end pr-3 transition-all duration-500`}
                      style={{ width: `${width}%`, minWidth: '40px' }}
                    >
                      <span className="text-xs font-bold text-white">{stage.value}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
